import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Activity, Flame, Star, Crosshair } from "lucide-react";
import { toast } from "sonner";
import AuthDialog from "@/components/AuthDialog";
import PaymentDialog from "@/components/PaymentDialog";
import { AssetCard, SearchResultCard } from "@/components/DashboardCards";
import AlertDropdown from "@/components/AlertDropdown";
import DashboardNav from "@/components/DashboardNav";
import CreditNudge from "@/components/CreditNudge";
import AlertSettingsDialog from "@/components/AlertSettingsDialog";
import DayTradingPanel from "@/components/DayTradingPanel";
import AISignalsTab from "@/components/AISignalsTab";
import MarketStatsStrip from "@/components/MarketStatsStrip";
import WatchlistTab from "@/components/tabs/WatchlistTab";
import HotForYouTab from "@/components/tabs/HotForYouTab";
import MuteDigestBanner from "@/components/MuteDigestBanner";
import { useMarketData, useWatchlist, useSignalAlerts, useSearch } from "@/hooks/useDashboardData";
import { useSignalFeeds } from "@/hooks/useSignalFeeds";
import { useAlertSettings } from "@/hooks/useAlertSettings";

function AssetGrid({ assets, loading, watchlist, priceTicks, onToggleWatchlist, onNavigate }) {
  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading market data...</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {assets.map((asset, index) => (
        <motion.div
          key={asset.symbol}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
        >
          <AssetCard asset={asset} isWatched={watchlist.includes(asset.symbol)} tick={priceTicks[asset.symbol]} onToggleWatchlist={onToggleWatchlist} onNavigate={onNavigate} />
        </motion.div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, hasSubscription, predictionCredits, logout, authHeaders } = useAuth();
  const { marketData, loading, priceTicks } = useMarketData();
  const { watchlist, toggleWatchlist } = useWatchlist(isAuthenticated);
  const { signalAlerts, unreadCount, markAlertsRead, dismissAlert } = useSignalAlerts(isAuthenticated);

  const [activeTab, setActiveTab] = useState("stocks");
  const [authDialog, setAuthDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { searchQuery, setSearchQuery, searchResult, setSearchResult, searchLoading, handleSearch } = useSearch(isAuthenticated, setAuthDialog, setPaymentDialog);
  const feeds = useSignalFeeds(isAuthenticated, authHeaders, activeTab);
  const { alertSettings, pushSupported, fetchAlertSettings, updateAlertSettings, togglePushNotifications } = useAlertSettings(isAuthenticated, authHeaders);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rh = params.get("robinhood");
    if (!rh) return;
    if (rh === "connected") toast.success("Robinhood connected — you can now place trades from AI signals");
    else toast.error("Robinhood connection failed — please try again");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const takeTrade = async (signal) => {
    if (!isAuthenticated) {
      setAuthDialog(true);
      return;
    }
    try {
      await axios.post(`${API}/performance/take-trade`, {
        symbol: signal.symbol,
        name: signal.name,
        asset_type: signal.type || signal.asset_type,
        signal: signal.signal,
        entry_price: signal.entry_price,
        target_price: signal.target_price,
        stop_loss: signal.stop_loss,
        win_probability: signal.win_probability,
        expected_return: signal.expected_return,
        risk_reward: signal.risk_reward
      }, { headers: authHeaders() });
      toast.success(`Trade logged: ${signal.signal} ${signal.symbol} @ $${signal.entry_price}`);
    } catch (error) {
      toast.error("Failed to log trade");
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] relative">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 via-transparent to-teal-900/5" />

      <DashboardNav
        navigate={navigate}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        hasSubscription={hasSubscription}
        predictionCredits={predictionCredits}
        logout={logout}
        onSignIn={() => setAuthDialog(true)}
        onUpgrade={() => setPaymentDialog(true)}
        alertBell={
          <AlertDropdown
            isOpen={alertsOpen}
            onToggle={() => setAlertsOpen(!alertsOpen)}
            onClose={() => setAlertsOpen(false)}
            onSettingsOpen={() => { setSettingsOpen(true); setAlertsOpen(false); fetchAlertSettings(); }}
            signalAlerts={signalAlerts}
            unreadCount={unreadCount}
            onMarkRead={markAlertsRead}
            onDismiss={dismissAlert}
            onTakeTrade={takeTrade}
          />
        }
      />

      {isAuthenticated && !hasSubscription && predictionCredits <= 1 && (
        <CreditNudge credits={predictionCredits} onTopUp={() => setPaymentDialog(true)} />
      )}

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: 'Unbounded' }}>
              MARKET OVERVIEW
            </h1>
          </div>
          <p className="text-gray-400">Real-time market data and AI-powered insights</p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-6"
        >
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                data-testid="search-input"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search any stock or crypto (e.g. TSLA, DOGE, SHIB)..."
                className="w-full pl-10 pr-4 py-3 bg-white/[0.03] border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:border-[#00E0FF]/50 text-sm"
              />
            </div>
            <Button
              data-testid="search-btn"
              type="submit"
              disabled={searchLoading || !searchQuery.trim()}
              className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold px-4 sm:px-6 shrink-0"
            >
              {searchLoading ? <Activity className="w-4 h-4 animate-spin" /> : "Predict"}
            </Button>
          </form>

          {/* Search Result */}
          <SearchResultCard searchResult={searchResult} onClose={() => setSearchResult(null)} />
        </motion.div>

        {/* Market Summary Cards */}
        <MarketStatsStrip marketData={marketData} />

        {/* Assets Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {isAuthenticated && (
            <MuteDigestBanner isAuthenticated={isAuthenticated} authHeaders={authHeaders} />
          )}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="glass-effect w-full justify-start mb-6 overflow-x-auto flex-nowrap">
              <TabsTrigger 
                data-testid="stocks-tab"
                value="stocks" 
                className="data-[state=active]:bg-[#00E0FF]/20 data-[state=active]:text-[#00E0FF]"
              >
                Stocks ({marketData.stocks.length})
              </TabsTrigger>
              {isAuthenticated && (
                <TabsTrigger
                  data-testid="hot-tab"
                  value="hot"
                  className="data-[state=active]:bg-[#FF3B30]/20 data-[state=active]:text-[#FF3B30] shrink-0"
                >
                  <Flame className="w-4 h-4 mr-2 fill-current" />
                  Hot for you
                </TabsTrigger>
              )}
              <TabsTrigger 
                data-testid="cryptos-tab"
                value="cryptos"
                className="data-[state=active]:bg-[#6366F1]/20 data-[state=active]:text-[#6366F1]"
              >
                Cryptocurrencies ({marketData.cryptos.length})
              </TabsTrigger>
              <TabsTrigger 
                data-testid="signals-tab"
                value="signals"
                className="data-[state=active]:bg-[#F59E0B]/20 data-[state=active]:text-[#F59E0B] shrink-0"
              >
                <Crosshair className="w-4 h-4 mr-2" />
                AI Signals
              </TabsTrigger>
              <TabsTrigger
                data-testid="day-trading-tab"
                value="day-trading"
                className="data-[state=active]:bg-[#FF3B30]/20 data-[state=active]:text-[#FF3B30] shrink-0"
              >
                <Flame className="w-4 h-4 mr-2" />
                Day Trading
              </TabsTrigger>
              {isAuthenticated && watchlist.length > 0 && (
                <TabsTrigger 
                  data-testid="watchlist-tab"
                  value="watchlist"
                  className="data-[state=active]:bg-[#F59E0B]/20 data-[state=active]:text-[#F59E0B] shrink-0"
                >
                  <Star className="w-4 h-4 mr-2 fill-current" />
                  Watchlist ({watchlist.length})
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="stocks" className="mt-0">
              <AssetGrid assets={marketData.stocks} loading={loading} watchlist={watchlist} priceTicks={priceTicks} onToggleWatchlist={toggleWatchlist} onNavigate={navigate} />
            </TabsContent>

            <TabsContent value="cryptos" className="mt-0">
              <AssetGrid assets={marketData.cryptos} loading={loading} watchlist={watchlist} priceTicks={priceTicks} onToggleWatchlist={toggleWatchlist} onNavigate={navigate} />
            </TabsContent>

            {/* AI Algorithm Signals Tab */}
            <TabsContent value="signals" className="mt-0">
              <AISignalsTab
                signals={feeds.signals}
                signalsLoading={feeds.signalsLoading}
                signalsError={feeds.signalsError}
                signalFilter={feeds.signalFilter}
                setSignalFilter={feeds.setSignalFilter}
                isAuthenticated={isAuthenticated}
                onSignIn={() => setAuthDialog(true)}
                onSubscribe={() => setPaymentDialog(true)}
                onTakeTrade={takeTrade}
                onNavigate={navigate}
              />
            </TabsContent>

            {/* Day Trading Tab */}
            <TabsContent value="day-trading" className="mt-0">
              <DayTradingPanel
                data={feeds.dayTrading}
                loading={feeds.dayTradingLoading}
                error={feeds.dayTradingError}
                isAuthenticated={isAuthenticated}
                onSignIn={() => setAuthDialog(true)}
                onSubscribe={() => setPaymentDialog(true)}
                onTakeTrade={takeTrade}
                navigate={navigate}
                watchlistOnly={feeds.dayTradingWatchlistOnly}
                onToggleWatchlistOnly={feeds.setDayTradingWatchlistOnly}
                hiddenSymbols={feeds.hiddenSymbols}
                onHideSymbol={feeds.hideSymbol}
                onUnhideSymbol={feeds.unhideSymbol}
                muteSuggestions={feeds.muteSuggestions}
                onAcceptSuggestion={feeds.muteSuggestionPermanent}
              />
            </TabsContent>

            {/* Watchlist Tab */}
            <TabsContent value="watchlist" className="mt-0">
              <WatchlistTab
                watchlist={watchlist}
                marketData={marketData}
                priceTicks={priceTicks}
                onToggleWatchlist={toggleWatchlist}
                navigate={navigate}
              />
            </TabsContent>

            {/* Hot for you Tab */}
            <TabsContent value="hot" className="mt-0">
              <HotForYouTab
                isAuthenticated={isAuthenticated}
                hasSubscription={hasSubscription}
                authHeaders={authHeaders}
                onSignIn={() => setAuthDialog(true)}
                onSubscribe={() => setPaymentDialog(true)}
                onTakeTrade={takeTrade}
                onNavigate={navigate}
              />
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>

      {/* Auth & Payment Dialogs */}
      <AuthDialog open={authDialog} onClose={() => setAuthDialog(false)} />
      <PaymentDialog open={paymentDialog} onClose={() => setPaymentDialog(false)} />

      {/* Alert Settings Dialog */}
      <AlertSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        alertSettings={alertSettings}
        onUpdate={updateAlertSettings}
        pushSupported={pushSupported}
        onTogglePush={togglePushNotifications}
      />
    </div>
  );
}
